import { API, Auth } from 'aws-amplify'
import { PaymentPlatform, DeviceType } from '../types/donation'

const API_NAME = 'DonationsAPI'

// Helper to get auth headers
const getAuthHeaders = async () => {
  const session = await Auth.currentSession()
  return {
    Authorization: `Bearer ${session.getIdToken().getJwtToken()}`
  }
}

// Billing details type (snake_case from API)
export interface BillingDetailsApi {
  first_name?: string
  last_name?: string
  address_line1?: string
  address_line2?: string
  city?: string
  postcode?: string
  country?: string
  email?: string
  phone?: string
}

// Billing details type (camelCase for frontend)
export interface BillingDetails {
  firstName?: string
  lastName?: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  postcode?: string
  country?: string
  email?: string
  phone?: string
}

// Gift Aid request type (snake_case for API)
export interface GiftAidRequestApi {
  donor_title?: string
  donor_first_name: string
  donor_last_name: string
  donor_address_line1: string
  donor_address_line2?: string
  donor_city: string
  donor_postcode: string
  donor_email?: string
}

// Gift Aid type (camelCase for frontend)
export interface GiftAidDetails {
  optedIn: boolean
  title?: string
  firstName: string
  lastName: string
  addressLine1: string
  addressLine2?: string
  city: string
  postcode: string
  email?: string
}

// API response types (snake_case from API)
export interface TransactionApiResponse {
  id: string
  amount: number
  platform: PaymentPlatform
  device?: DeviceType
  donation_type_id: string
  campaign_id?: string
  date: string
  external_id?: string
  transaction_code?: string
  reference?: string
  billing?: BillingDetailsApi
  frequency?: string
  stripe_customer_id?: string
  stripe_payment_method_id?: string
  stripe_subscription_id?: string
  stripe_payment_intent_id?: string
  card_last_four?: string
  card_brand?: string
  donor_email?: string
  subscription_reference?: string
  next_charge_date?: string
  subscription_status?: string
  parent_transaction_id?: string
  failure_count?: number
  failure_reason?: string
  gift_aid?: boolean
  gift_aid_declaration_id?: string
  receipt_s3_key?: string
  created_at: string
  updated_at: string
}

// API request type for creating transactions
export interface TransactionCreateRequest {
  amount: number
  platform: PaymentPlatform
  donation_type_id: string
  reference?: string
  billing?: BillingDetailsApi
  gift_aid?: GiftAidRequestApi
  timestamp?: string // ISO 8601 format for historical transactions
}

// API request type for updating transactions
export interface TransactionUpdateRequest {
  donation_type_id?: string
  billing?: BillingDetailsApi
  subscription_status?: string
}

// Analytics types (from API)
export interface TransactionSummaryApi {
  total_amount: number
  total_transactions: number
}

export interface DonationTypeSummaryApi {
  total_amount: number
  total_transactions: number
  by_platform: Record<string, TransactionSummaryApi>
}

export interface GiftAidStatsApi {
  eligible_amount: number
  eligible_transactions: number
  claimable_amount: number
}

export interface TransactionBreakdownApi {
  by_platform: Record<string, TransactionSummaryApi>
  by_donation_type: Record<string, DonationTypeSummaryApi>
  total: TransactionSummaryApi
  gift_aid: GiftAidStatsApi
}

// Analytics types (frontend camelCase)
export interface AnalyticsSummary {
  totalAmount: number
  totalTransactions: number
}

export interface DonationTypeSummary {
  totalAmount: number
  totalTransactions: number
  byPlatform: Record<string, AnalyticsSummary>
}

export interface GiftAidStats {
  eligibleAmount: number
  eligibleTransactions: number
  claimableAmount: number
}

export interface TransactionBreakdown {
  byPlatform: Record<string, AnalyticsSummary>
  byDonationType: Record<string, DonationTypeSummary>
  total: AnalyticsSummary
  giftAid: GiftAidStats
}

export interface AnalyticsFilter {
  startDate?: string
  endDate?: string
  campaignId?: string
  donationTypeId?: string
}

// Frontend type (camelCase)
export interface Transaction {
  id: string
  amount: number
  platform: PaymentPlatform
  device?: DeviceType
  donationTypeId: string
  campaignId?: string
  date: string
  externalId?: string
  transactionCode?: string
  reference?: string
  billing?: BillingDetails
  frequency?: string
  donorEmail?: string
  subscriptionReference?: string
  nextChargeDate?: string
  subscriptionStatus?: string
  parentTransactionId?: string
  failureCount?: number
  failureReason?: string
  stripeSubscriptionId?: string
  cardLastFour?: string
  cardBrand?: string
  giftAid?: boolean
  giftAidDeclarationId?: string
  receiptS3Key?: string
  createdAt: string
  updatedAt?: string
}

// Frontend type for creating transactions
export interface TransactionCreate {
  amount: number
  platform: PaymentPlatform
  donationTypeId: string
  date: string // YYYY-MM-DD format
  reference?: string
  billing?: BillingDetails
  giftAid?: GiftAidDetails
}

// Frontend type for updating transactions
export interface TransactionUpdate {
  donationTypeId?: string
  billing?: BillingDetails
  subscriptionStatus?: string
}

// Filter options for listing transactions
export interface TransactionFilter {
  platform?: PaymentPlatform
  donationTypeId?: string
  campaignId?: string
  startDate?: string
  endDate?: string
  giftAid?: boolean
  limit?: number
}

// Transform billing details from API to frontend
const transformBillingDetails = (billing?: BillingDetailsApi): BillingDetails | undefined => {
  if (!billing) return undefined
  return {
    firstName: billing.first_name,
    lastName: billing.last_name,
    addressLine1: billing.address_line1,
    addressLine2: billing.address_line2,
    city: billing.city,
    postcode: billing.postcode,
    country: billing.country,
    email: billing.email,
    phone: billing.phone,
  }
}

// Transform billing details from frontend to API
const transformBillingDetailsToApi = (billing?: BillingDetails): BillingDetailsApi | undefined => {
  if (!billing) return undefined
  // Only include if at least one field has a value
  const hasValues = billing.firstName || billing.lastName || billing.addressLine1 ||
    billing.city || billing.postcode || billing.email || billing.phone
  if (!hasValues) return undefined
  return {
    first_name: billing.firstName || undefined,
    last_name: billing.lastName || undefined,
    address_line1: billing.addressLine1 || undefined,
    address_line2: billing.addressLine2 || undefined,
    city: billing.city || undefined,
    postcode: billing.postcode || undefined,
    country: billing.country || undefined,
    email: billing.email || undefined,
    phone: billing.phone || undefined,
  }
}

// Transform Gift Aid details from frontend to API
const transformGiftAidToApi = (giftAid?: GiftAidDetails): GiftAidRequestApi | undefined => {
  if (!giftAid || !giftAid.optedIn) return undefined
  return {
    donor_title: giftAid.title || undefined,
    donor_first_name: giftAid.firstName,
    donor_last_name: giftAid.lastName,
    donor_address_line1: giftAid.addressLine1,
    donor_address_line2: giftAid.addressLine2 || undefined,
    donor_city: giftAid.city,
    donor_postcode: giftAid.postcode,
    donor_email: giftAid.email || undefined,
  }
}

// Transform API response to frontend type
const transformApiResponse = (response: TransactionApiResponse): Transaction => ({
  id: response.id,
  amount: response.amount,
  platform: response.platform,
  device: response.device,
  donationTypeId: response.donation_type_id,
  campaignId: response.campaign_id,
  date: response.date,
  externalId: response.external_id,
  transactionCode: response.transaction_code,
  reference: response.reference,
  billing: transformBillingDetails(response.billing),
  frequency: response.frequency,
  donorEmail: response.donor_email,
  subscriptionReference: response.subscription_reference,
  nextChargeDate: response.next_charge_date,
  subscriptionStatus: response.subscription_status,
  parentTransactionId: response.parent_transaction_id,
  failureCount: response.failure_count,
  failureReason: response.failure_reason,
  stripeSubscriptionId: response.stripe_subscription_id,
  cardLastFour: response.card_last_four,
  cardBrand: response.card_brand,
  giftAid: response.gift_aid,
  giftAidDeclarationId: response.gift_aid_declaration_id,
  receiptS3Key: response.receipt_s3_key,
  createdAt: response.created_at,
  updatedAt: response.updated_at,
})

export const transactionsApi = {
  // List transactions with optional filters
  async listTransactions(filter?: TransactionFilter): Promise<Transaction[]> {
    const headers = await getAuthHeaders()

    // Build query string
    const params = new URLSearchParams()
    if (filter?.platform) params.append('platform', filter.platform)
    if (filter?.donationTypeId) params.append('donation_type_id', filter.donationTypeId)
    if (filter?.campaignId) params.append('campaign_id', filter.campaignId)
    if (filter?.startDate) params.append('start_date', filter.startDate)
    if (filter?.endDate) params.append('end_date', filter.endDate)
    if (filter?.giftAid !== undefined) params.append('gift_aid', filter.giftAid.toString())
    if (filter?.limit) params.append('limit', filter.limit.toString())

    const queryString = params.toString()
    const path = queryString ? `/transactions?${queryString}` : '/transactions'

    const response: TransactionApiResponse[] | null = await API.get(API_NAME, path, { headers })
    return (response || []).map(transformApiResponse)
  },

  // Get a single transaction by ID
  async getTransaction(id: string): Promise<Transaction> {
    const headers = await getAuthHeaders()
    const response: TransactionApiResponse = await API.get(API_NAME, `/transactions/${id}`, { headers })
    return transformApiResponse(response)
  },

  // Create a new transaction (used for manual bank transfer entry)
  async createTransaction(transaction: TransactionCreate): Promise<Transaction> {
    const headers = await getAuthHeaders()

    const body: TransactionCreateRequest = {
      amount: transaction.amount,
      platform: transaction.platform,
      donation_type_id: transaction.donationTypeId,
      reference: transaction.reference || undefined,
      billing: transformBillingDetailsToApi(transaction.billing),
      gift_aid: transformGiftAidToApi(transaction.giftAid),
    }

    const response: TransactionApiResponse = await API.post(API_NAME, '/transactions', { headers, body })
    return transformApiResponse(response)
  },

  // Update a transaction (billing details and donation type only)
  async updateTransaction(id: string, update: TransactionUpdate): Promise<Transaction> {
    const headers = await getAuthHeaders()

    const body: TransactionUpdateRequest = {
      donation_type_id: update.donationTypeId || undefined,
      billing: transformBillingDetailsToApi(update.billing),
      subscription_status: update.subscriptionStatus || undefined,
    }

    const response: TransactionApiResponse = await API.put(API_NAME, `/transactions/${id}`, { headers, body })
    return transformApiResponse(response)
  },

  // Delete a transaction
  async deleteTransaction(id: string): Promise<void> {
    const headers = await getAuthHeaders()
    await API.del(API_NAME, `/transactions/${id}`, { headers })
  },

  // Generate receipt for a transaction
  async generateReceipt(id: string): Promise<{ message: string; transactionId: string }> {
    const headers = await getAuthHeaders()
    return await API.post(API_NAME, `/transactions/${id}/receipt`, { headers })
  },

  // Create Gift Aid declaration for a transaction
  async createGiftAid(transactionId: string, giftAid: GiftAidDetails): Promise<any> {
    const headers = await getAuthHeaders()
    const body = transformGiftAidToApi(giftAid)
    return await API.post(API_NAME, `/transactions/${transactionId}/gift-aid`, { headers, body })
  },

  // Get Gift Aid declaration PDF URL
  async getGiftAidPdfUrl(declarationId: string): Promise<string> {
    const headers = await getAuthHeaders()
    const response = await API.get(API_NAME, `/gift-aid/${declarationId}/pdf`, { headers })
    return response.url
  },

  // Get Receipt PDF URL
  async getReceiptPdfUrl(transactionId: string): Promise<string> {
    const headers = await getAuthHeaders()
    const response = await API.get(API_NAME, `/receipts/${transactionId}/pdf`, { headers })
    return response.url
  },

  // Get transaction analytics/breakdown
  async getAnalytics(filter?: AnalyticsFilter): Promise<TransactionBreakdown> {
    const headers = await getAuthHeaders()

    // Build query string
    const params = new URLSearchParams()
    if (filter?.startDate) params.append('start_date', filter.startDate)
    if (filter?.endDate) params.append('end_date', filter.endDate)
    if (filter?.campaignId) params.append('campaign_id', filter.campaignId)
    if (filter?.donationTypeId) params.append('donation_type_id', filter.donationTypeId)

    const queryString = params.toString()
    const path = queryString ? `/transactions/analytics?${queryString}` : '/transactions/analytics'

    const response: TransactionBreakdownApi | null = await API.get(API_NAME, path, { headers })

    // Transform API response to frontend format
    const transformSummary = (summary: TransactionSummaryApi): AnalyticsSummary => ({
      totalAmount: summary.total_amount,
      totalTransactions: summary.total_transactions,
    })

    const transformPlatformMap = (apiMap: Record<string, TransactionSummaryApi> | null): Record<string, AnalyticsSummary> => {
      if (!apiMap) return {}
      const result: Record<string, AnalyticsSummary> = {}
      for (const [key, value] of Object.entries(apiMap)) {
        result[key] = transformSummary(value)
      }
      return result
    }

    const transformDonationTypeMap = (apiMap: Record<string, DonationTypeSummaryApi> | null): Record<string, DonationTypeSummary> => {
      if (!apiMap) return {}
      const result: Record<string, DonationTypeSummary> = {}
      for (const [key, value] of Object.entries(apiMap)) {
        result[key] = {
          totalAmount: value.total_amount,
          totalTransactions: value.total_transactions,
          byPlatform: transformPlatformMap(value.by_platform),
        }
      }
      return result
    }

    const transformGiftAid = (giftAid: GiftAidStatsApi | null): GiftAidStats => {
      if (!giftAid) return { eligibleAmount: 0, eligibleTransactions: 0, claimableAmount: 0 }
      return {
        eligibleAmount: giftAid.eligible_amount,
        eligibleTransactions: giftAid.eligible_transactions,
        claimableAmount: giftAid.claimable_amount,
      }
    }

    if (!response) {
      return {
        byPlatform: {},
        byDonationType: {},
        total: { totalAmount: 0, totalTransactions: 0 },
        giftAid: { eligibleAmount: 0, eligibleTransactions: 0, claimableAmount: 0 },
      }
    }

    return {
      byPlatform: transformPlatformMap(response.by_platform),
      byDonationType: transformDonationTypeMap(response.by_donation_type),
      total: transformSummary(response.total),
      giftAid: transformGiftAid(response.gift_aid),
    }
  },
}

export default transactionsApi
